import layoutEngine from "../../libraries/layoutEngine";
import ProjectManager from "../../canvas/ProjectManager";
import ToolManager from "../../libraries/ToolManager";
import { normalizePatternKey, generateNextPCellName } from "./parametricNamingService";
import { rebuildPCellFromDefinition } from "./pcellInterpreter";
import {
  registerPlacedPCell,
  getPlacedPCell,
  updatePlacedPCell,
  removePlacedPCell,
} from "../store/placedPCellStore";




let placementListenerBound = false;
let activeDraftContext = null;
let isAutoPreparingNext = false;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function bindPlacementListener() {
  if (placementListenerBound) return;
  placementListenerBound = true;

  window.addEventListener("instance-placed", async (e) => {
    const placedRef = e.detail?.ref;
    if (!placedRef || !activeDraftContext) return;

    const currentDraftCellName = activeDraftContext.cellName;

    // sirf current draft preview place hone par hi react kare
    if (placedRef !== currentDraftCellName) return;
    if (isAutoPreparingNext) return;

    // draft ab actual placed ho chuka hai
    // draft ab actual placed ho chuka hai
updatePlacedPCell(currentDraftCellName, {
  isDraftPreview: false,
});

// ✅ Ab actual placed PCell ko BlocksTab me add karo
if (typeof activeDraftContext.onPlacedPreview === "function") {
  activeDraftContext.onPlacedPreview(currentDraftCellName);
}

    const nextContext = {
  patternType: activeDraftContext.patternType,
  topCellName: activeDraftContext.topCellName,
  parameters: activeDraftContext.parameters,
  definitionSnapshot: activeDraftContext.definitionSnapshot,
  onPlacedPreview: activeDraftContext.onPlacedPreview,
};

    activeDraftContext = null;
    isAutoPreparingNext = true;

    try {
      await startPCellPlacement({
        patternType: nextContext.patternType,
        topCellName: nextContext.topCellName,
        parameters: nextContext.parameters,
        onPlacedPreview: nextContext.onPlacedPreview,
        isDraftPreview: true,
      });
    } catch (err) {
      console.error("❌ Failed to prepare next PCell preview:", err);
    } finally {
      isAutoPreparingNext = false;
    }
  });
}

export function getActiveDraftPCell() {
  return activeDraftContext ? { ...activeDraftContext } : null;
}

export function clearActiveDraftPCell() {
  activeDraftContext = null;
}

function normalizePCellLocalOriginToBottomLeft(cellName) {
  const cell = layoutEngine.getCellByName?.(cellName);
  if (!cell) return;

  const elements = Array.isArray(cell.elements) ? cell.elements : [];
  if (!elements.length) return;

  let minX = Infinity;
  let maxY = -Infinity;

  for (const el of elements) {
    const x1 = Number(el.x1 ?? el.x ?? 0);
    const x2 = Number(el.x2 ?? ((el.x ?? 0) + (el.width ?? 0)));

    const y1 = Number(el.y1 ?? el.y ?? 0);
    const y2 = Number(el.y2 ?? ((el.y ?? 0) + (el.height ?? 0)));

    minX = Math.min(minX, x1, x2);

    // ✅ canvas/world me bottom-left anchor ke liye bottom = maxY
    maxY = Math.max(maxY, y1, y2);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(maxY)) return;

  const dx = -minX;

  // ✅ bottom-left ko local (0,0) par lao
  // iske baad geometry negative Y side/upar rahegi
  const dy = -maxY;

  for (const el of elements) {
    if ("x1" in el) el.x1 = Number(el.x1 || 0) + dx;
    if ("x2" in el) el.x2 = Number(el.x2 || 0) + dx;
    if ("x" in el) el.x = Number(el.x || 0) + dx;

    if ("y1" in el) el.y1 = Number(el.y1 || 0) + dy;
    if ("y2" in el) el.y2 = Number(el.y2 || 0) + dy;
    if ("y" in el) el.y = Number(el.y || 0) + dy;
  }
}

export function cancelDraftPCellPlacement() {
  const draft = activeDraftContext;
  if (!draft?.cellName) return false;

  const placed = getPlacedPCell(draft.cellName);

  // agar draft already placed ho chuka hai to delete mat karo
  if (placed && placed.isDraftPreview === false) {
    activeDraftContext = null;
    return false;
  }

  removePlacedPCell(draft.cellName);
  layoutEngine.deleteCell?.(draft.cellName);

  if (ProjectManager.getSelectedInstanceCell?.() === draft.cellName) {
    ProjectManager.setSelectedInstanceCell?.("");
  }

  window.dispatchEvent(new Event("hierarchy-updated"));
  window.canvasEngine?.draw?.();

  activeDraftContext = null;
  return true;
}

export async function startPCellPlacement({
  patternType,
  topCellName,
  parameters = [],
  onPlacedPreview,
  isDraftPreview = true,
}) {
  if (!topCellName || !String(topCellName).trim()) {
    throw new Error("Top cell name is required");
  }

  if (!patternType || !String(patternType).trim()) {
    throw new Error("Pattern type is required");
  }

  bindPlacementListener();

  const nextCellName = generateNextPCellName(patternType);
  const patternKey = normalizePatternKey(patternType);

  // ✅ backend se full pattern definition lao
    const definitionRes = await fetch(
    `${API_BASE_URL}/parametric/designs/${encodeURIComponent(patternType)}`
  );
  const definitionData = await definitionRes.json();

  const definitionSnapshot =
    definitionData?.data?.design && Array.isArray(definitionData.data.design)
      ? definitionData.data
      : definitionData?.design && Array.isArray(definitionData.design)
      ? definitionData
      : null;

    if (!definitionSnapshot) {
    throw new Error(`Invalid pattern definition received for "${patternType}"`);
  }

  // Initial placement and every later parameter update must use the same
  // definition-driven geometry pipeline. This keeps anchors and orientation
  // stable for asymmetric as well as future patterns.
  rebuildPCellFromDefinition(nextCellName, definitionSnapshot, parameters);

  
  normalizePCellLocalOriginToBottomLeft(nextCellName);
  registerPlacedPCell({
    cellName: nextCellName,
    patternType,
    patternKey,
    parentCellName: topCellName,
    placementType: "pcell",
    parameters,
    definitionSnapshot,
    dots: {
      gridSize: null,
      visible: true,
    },
    connections: [],
    isDraftPreview,
  });

  activeDraftContext = {
    cellName: nextCellName,
    patternType,
    patternKey,
    topCellName,
    parameters: Array.isArray(parameters)
      ? JSON.parse(JSON.stringify(parameters))
      : [],
    definitionSnapshot: definitionSnapshot
      ? JSON.parse(JSON.stringify(definitionSnapshot))
      : null,
    onPlacedPreview,
  };

  ProjectManager.setSelectedCell(topCellName);
  ProjectManager.setSelectedInstanceCell(nextCellName);
  ProjectManager.setSelectedPattern?.(patternType);

  ToolManager.setTool("instance");
  window.dispatchEvent(new CustomEvent("tool-changed", { detail: "instance" }));

  if (!isDraftPreview && typeof onPlacedPreview === "function") {
  onPlacedPreview(nextCellName);
}

  window.dispatchEvent(new Event("hierarchy-updated"));
  window.canvasEngine?.draw?.();

  return {
    patternType,
    patternKey,
    cellName: nextCellName,
    placementType: "pcell",
  };
}
